import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  Lightbulb,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Download,
  Settings,
  FileDown,
  Palette,
  Upload,
  Database,
  Monitor,
  ChevronDown,
  Search,
  Shield,
  Map,
  BarChart3,
  Layers,
  Package,
  BookOpen,
  GitFork,
  Users
} from 'lucide-react';
import { processOptimizationAPI, OptimizationAnalysis, OptimizationOpportunity, ProcessSkillPreview } from '../services/processOptimizationAPI';
import VerificationBadge from '../components/Verification/VerificationBadge';
import VerificationPanel from '../components/Verification/VerificationPanel';
import VerificationToggle from '../components/Verification/VerificationToggle';
import OpportunityCard from '../components/ProcessOptimization/OpportunityCard';
import ExecutiveSummary from '../components/ProcessOptimization/ExecutiveSummary';
import ExecutiveStory from '../components/ProcessOptimization/ExecutiveStory';
import PrioritizationMatrix from '../components/ProcessOptimization/PrioritizationMatrix';
import GanttRoadmap from '../components/ProcessOptimization/GanttRoadmap';
import MetricsImportModal from '../components/ProcessOptimization/MetricsImportModal';
import GroundedStatusBadge, { GroundedProgress } from '../components/ProcessOptimization/GroundedStatusBadge';
import PortfolioBenefitsRollup from '../components/ProcessOptimization/PortfolioBenefitsRollup';
import { metricsImportAPI, GroundedStatus, ImportHistoryEntry } from '../services/metricsImportAPI';
import { useProject } from '../contexts/ProjectContext';
import toast from 'react-hot-toast';
import * as XLSX from '../utils/xlsxCompat';
import { generateMetricsTemplate } from '../utils/metricsTemplateGenerator';
import { generateAllOpportunitiesReport } from '../services/reportGenerators';
import SmartRouterBanner from '../components/SmartRouter/SmartRouterBanner';
import OrchestratorProjectMismatchBanner from '../components/SmartRouter/OrchestratorProjectMismatchBanner';
import { useOrchestratorContext } from '../hooks/useOrchestratorContext';
import { useSmartRouterStepSync } from '../hooks/useSmartRouterStepSync';

const sortByDependencies = (opportunities: OptimizationOpportunity[]): OptimizationOpportunity[] => {
  if (!opportunities || opportunities.length === 0) return [];

  const result: OptimizationOpportunity[] = [];
  const added = new Set<number>();
  const remaining = [...opportunities];

  const getPrerequisiteIds = (opp: OptimizationOpportunity): number[] => {
    if (!opp.dependencies?.prerequisites) return [];
    return opp.dependencies.prerequisites
      .map(p => p.prerequisite_opportunity?.id || p.id)
      .filter((id): id is number => id !== undefined && id !== null);
  };

  const canBeAdded = (opp: OptimizationOpportunity): boolean => {
    const prereqIds = getPrerequisiteIds(opp);
    if (prereqIds.length === 0) return true;
    return prereqIds.every(prereqId => added.has(prereqId));
  };

  let iterations = 0;
  const maxIterations = opportunities.length * 2;

  while (remaining.length > 0 && iterations < maxIterations) {
    iterations++;
    let addedThisRound = false;

    for (let i = remaining.length - 1; i >= 0; i--) {
      const opp = remaining[i];
      if (canBeAdded(opp)) {
        result.push(opp);
        added.add(opp.id);
        remaining.splice(i, 1);
        addedThisRound = true;
      }
    }

    if (!addedThisRound && remaining.length > 0) {
      const next = remaining.shift()!;
      result.push(next);
      added.add(next.id);
    }
  }

  return result;
};

/**
 * Detect a pre-redesign analysis whose rows were backfilled with placeholder
 * values for the new schema columns but never reprocessed through the new
 * disposition/citation engine (Asana 1218070487641442, [3], Ryan's
 * 2026-09-07 carried-over finding: project 740 "GCS manual US onboarding" —
 * 44 opportunities uniformly disposition="automate", citations=[], no
 * 'dispositioned' decision_trail event — caused the Assess step to spin
 * indefinitely). A real, engine-assessed analysis always has at least one
 * opportunity with a citation or a recorded 'dispositioned' trail event;
 * this only trips on the fully-empty backfilled shape.
 */
const isLegacyUngroundedAnalysis = (opportunities: OptimizationOpportunity[]): boolean => {
  if (!opportunities || opportunities.length === 0) return false;
  return opportunities.every(o => {
    const hasCitations = Array.isArray(o.citations) && o.citations.length > 0;
    const hasBeenDispositioned = Array.isArray(o.decision_trail) && o.decision_trail.some(e => e.event === 'dispositioned');
    return !hasCitations && !hasBeenDispositioned;
  });
};

type ViewMode = 'all' | 'business' | 'compliance' | 'matrix' | 'roadmap';

const ProcessOptimization: React.FC = () => {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const { currentProject } = useProject();
  const navigate = useNavigate();
  const orchestratorCtx = useOrchestratorContext();
  const smartRouter = useSmartRouterStepSync();

  const projectId = urlProjectId || currentProject?.id?.toString();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<OptimizationAnalysis | null>(null);
  const [verificationMode, setVerificationMode] = useState<'off' | 'standard' | 'deep'>('off');
  const [verificationMetadata, setVerificationMetadata] = useState<any>(null);
  const [showVerificationPanel, setShowVerificationPanel] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('processOptimization_viewMode');
    return (saved as ViewMode) || 'all';
  });
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const prefetchedRef = useRef(false);
  const [groundedStatus, setGroundedStatus] = useState<GroundedStatus | null>(null);
  const [importHistory, setImportHistory] = useState<ImportHistoryEntry[]>([]);
  const [isDownloadingPptx, setIsDownloadingPptx] = useState(false);
  // Bumped whenever opportunity records change (regenerate, disposition override) so the exec story re-reads them.
  const [storyRefreshKey, setStoryRefreshKey] = useState(0);
  const [mainTab, setMainTab] = useState<'opportunities' | 'matrix' | 'roadmap'>('opportunities');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [reviewedIds, setReviewedIds] = useState<Set<number>>(new Set());
  const [dispositions, setDispositions] = useState<Record<number, 'automate' | 'skill_gpt' | 'keep_human' | 'do_not_change'>>({});
  const [generatingBundles, setGeneratingBundles] = useState<Record<number, boolean>>({});
  const [approvingSkills, setApprovingSkills] = useState<Record<number, boolean>>({});
  const [skillGate, setSkillGate] = useState<Record<number, { status: string; failures?: { check: string; reason: string; control_id?: string; control_name?: string; framework?: string }[] }>>({});
  const [skillRegistry, setSkillRegistry] = useState<Awaited<ReturnType<typeof processOptimizationAPI.getSkillRegistry>>>([]);
  const [registryLoading, setRegistryLoading] = useState(false);
  const [registryBusy, setRegistryBusy] = useState<Record<number, boolean>>({});
  const [processSkill, setProcessSkill] = useState<ProcessSkillPreview | null>(null);
  const [processSkillBusy, setProcessSkillBusy] = useState<'preview' | 'download' | null>(null);
  const [processSkillError, setProcessSkillError] = useState<string | null>(null);
  // Pre-redesign backfilled analysis (Asana 1218070487641442 carried-over finding).
  const [isLegacyUngrounded, setIsLegacyUngrounded] = useState(false);

  const loadSkillRegistry = useCallback(async () => {
    if (!projectId) return;
    setRegistryLoading(true);
    try {
      setSkillRegistry(await processOptimizationAPI.getSkillRegistry(Number(projectId)));
    } catch {
      setSkillRegistry([]);
    } finally {
      setRegistryLoading(false);
    }
  }, [projectId]);

  const updateGovernance = async (oppId: number, updates: { owner?: string; status?: 'draft' | 'deprecated'; mark_reviewed?: boolean }) => {
    setRegistryBusy(prev => ({ ...prev, [oppId]: true }));
    try {
      const result = await processOptimizationAPI.updateSkillGovernance(oppId, updates);
      setSkillRegistry(prev => prev.map(r => (r.id === oppId ? { ...r, ...result } : r)));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Governance update failed');
    } finally {
      setRegistryBusy(prev => ({ ...prev, [oppId]: false }));
    }
  };

  useEffect(() => {
    if (projectId) {
      loadAnalysis();
      loadGroundedStatus();
      loadImportHistory();
    }
  }, [projectId]);

  const loadGroundedStatus = async () => {
    try {
      const status = await metricsImportAPI.getGroundedStatus(parseInt(projectId!));
      setGroundedStatus(status);
    } catch (error) {
      console.error('Error loading grounded status:', error);
    }
  };

  const loadImportHistory = async () => {
    try {
      const history = await metricsImportAPI.getImportHistory(parseInt(projectId!));
      setImportHistory(history);
    } catch (error) {
      console.error('Error loading import history:', error);
    }
  };

  const handleImportComplete = async () => {
    await Promise.all([loadAnalysis(), loadGroundedStatus(), loadImportHistory()]);
    setWizardStep(4); // Govern
  };

  // Concurrency cap for the compliance pre-warm below — unthrottled, this
  // fired 2 requests (one of them an LLM-backed SOD call) per opportunity
  // simultaneously via a plain forEach. On a large project that's dozens of
  // concurrent requests fired in one tick; on the legacy-data project that
  // surfaced Ryan's 2026-09-07 finding (44 opportunities) that was 88 at
  // once. Batching keeps the same "warm the cache" behavior without the burst.
  const PREFETCH_BATCH_SIZE = 6;

  const prefetchComplianceData = async (opportunities: OptimizationOpportunity[], pid: number) => {
    if (prefetchedRef.current || !opportunities?.length) return;
    prefetchedRef.current = true;

    // Fire-and-forget: warm up DB cache for MRM + SOD for each opportunity,
    // in small batches. Small initial delay so page render isn't blocked.
    setTimeout(async () => {
      for (let i = 0; i < opportunities.length; i += PREFETCH_BATCH_SIZE) {
        const batch = opportunities.slice(i, i + PREFETCH_BATCH_SIZE);
        await Promise.all(batch.map(opp => Promise.all([
          // MRM classification (GET)
          fetch(`/api/process-optimization/${pid}/opportunities/${opp.id}/mrm-classification`, {
            credentials: 'include'
          }).catch(() => {}),

          // SOD analysis (POST)
          fetch(`/api/process-optimization/${pid}/opportunities/${opp.id}/sod-analysis`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              affectedSteps: opp.affected_steps || [],
              solutionApproach: opp.solution_approach
            })
          }).catch(() => {}),
        ])));
      }
    }, 1500);
  };

  const loadAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await processOptimizationAPI.getAnalysis(parseInt(projectId!));

      console.log('[ProcessOptimization] API Response:', result);
      console.log('[ProcessOptimization] Analysis data:', result.data);
      if (result.data?.opportunities && result.data.opportunities.length > 0) {
        const firstOpp = result.data.opportunities[0];
        console.log('[ProcessOptimization] First opportunity:', {
          id: firstOpp.id,
          title: firstOpp.title,
          governance_complexity_score: firstOpp.governance_complexity_score,
          automation_readiness_score: firstOpp.automation_readiness_score,
          integration_complexity_score: firstOpp.integration_complexity_score,
          regulatory_risk_score: firstOpp.regulatory_risk_score,
          has_phased_plan: !!firstOpp.phased_rollout_plan,
          phased_plan_keys: firstOpp.phased_rollout_plan ? Object.keys(firstOpp.phased_rollout_plan) : []
        });
      }

      if (result.needsGeneration) {
        // No analysis exists — start at Step 1
        setWizardStep(1);
      } else {
        setAnalysis(result.data!);
        // Seed dispositions from DB values (engine-assigned, user can override)
        const seeded: Record<number, 'automate' | 'skill_gpt' | 'keep_human' | 'do_not_change'> = {};
        (result.data!.opportunities || []).forEach((opp: any) => {
          if (opp.disposition && ['automate', 'skill_gpt', 'keep_human', 'do_not_change'].includes(opp.disposition)) {
            seeded[opp.id] = opp.disposition;
          }
        });
        setDispositions(seeded);
        // Do NOT set wizardStep here — computedWizardStep + useEffect handle auto-advance

        const legacy = isLegacyUngroundedAnalysis(result.data!.opportunities || []);
        setIsLegacyUngrounded(legacy);
        // Pre-fetch compliance data in background so Compliance tab is instant —
        // skipped for a legacy backfilled analysis: nobody is actively working
        // this data, and it's exactly the shape that turned a 44-opportunity
        // burst of MRM/SOD pre-warm calls into a frozen tab (see the banner
        // rendered below instead).
        if (!legacy) {
          prefetchComplianceData(result.data!.opportunities, parseInt(projectId!));
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Human disposition override. Persisted server-side with who/when/reason on the
   * opportunity's decision trail; `null` reverts to the engine's decision.
   */
  const persistDisposition = async (
    opportunity: OptimizationOpportunity,
    disposition: 'automate' | 'skill_gpt' | 'keep_human' | 'do_not_change' | null
  ) => {
    const engineDisposition = (opportunity.decision_trail || []).slice().reverse().find(e => e.event === 'dispositioned')?.disposition;
    const isOverride = disposition !== null && disposition !== engineDisposition;
    let reason: string | undefined;
    if (isOverride) {
      const entered = window.prompt(`Override the engine disposition (${engineDisposition || opportunity.disposition}) → ${disposition}.\nReason (recorded on the audit trail):`, '');
      if (entered === null) return; // cancelled
      reason = entered.trim() || undefined;
    }
    try {
      const updated = await processOptimizationAPI.updateDisposition(parseInt(projectId!), opportunity.id, disposition, reason);
      setDispositions(prev => ({ ...prev, [opportunity.id]: updated.disposition as any }));
      setAnalysis(prev => prev ? {
        ...prev,
        opportunities: (prev.opportunities || []).map(o => o.id === opportunity.id
          ? { ...o, disposition: updated.disposition, disposition_rationale: updated.disposition_rationale, disposition_hard_stop: updated.disposition_hard_stop, decision_trail: updated.decision_trail }
          : o)
      } : prev);
      setStoryRefreshKey(k => k + 1);
      toast.success(disposition === null ? 'Reverted to engine disposition' : `Disposition saved: ${updated.disposition}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save disposition');
    }
  };

  const runAnalysis = async (forceRegenerate: boolean) => {
    try {
      setAnalyzing(true);
      setError(null);

      const result = await processOptimizationAPI.analyzeProject(
        parseInt(projectId!),
        undefined,
        forceRegenerate,
        { verify: verificationMode !== 'off', mode: verificationMode }
      );

      setAnalysis(result.data);
      setVerificationMetadata(result.verification_metadata || null);
      if (result.hash_gate?.status === 'unchanged') {
        // Hash gate: same steps, evidence, model and rule versions → same assessment.
        // The previous run (same dispositions, same citation set) is reused instead of re-asking the model.
        toast.success('Inputs unchanged since the last assessment — reused it (same dispositions and citations).', { duration: 6000 });
      }
      // Seed dispositions from engine-assigned values in new analysis
      const seeded: Record<number, 'automate' | 'skill_gpt' | 'keep_human' | 'do_not_change'> = {};
      (result.data?.opportunities || []).forEach((opp: any) => {
        if (opp.disposition && ['automate', 'skill_gpt', 'keep_human', 'do_not_change'].includes(opp.disposition)) {
          seeded[opp.id] = opp.disposition;
        }
      });
      setDispositions(seeded);
      setStoryRefreshKey(k => k + 1);
      setWizardStep(2); // advance to Disposition after analysis
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to run analysis';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setAnalyzing(false);
    }
  };

  const getInefficiencyIcon = (type: string) => {
    const icons: any = {
      'redundancy': '🔄',
      'sequential_bottleneck': '⚡',
      'manual_pattern': '✋',
      'loop_detection': '🔁',
      'handoff_delay': '🤝',
      'exception_handling': '⚠️'
    };
    return icons[type] || '📋';
  };

  const getInefficiencyLabel = (type: string) => {
    const labels: any = {
      'redundancy': 'Redundancy',
      'sequential_bottleneck': 'Sequential Bottleneck',
      'manual_pattern': 'Manual Process',
      'loop_detection': 'Rework Loop',
      'handoff_delay': 'Handoff Delay',
      'exception_handling': 'Exception Handling'
    };
    return labels[type] || type;
  };

  const exportToExcel = async () => {
    if (!analysis || !analysis.opportunities || analysis.opportunities.length === 0) {
      return;
    }

    // Format data for Excel — include grounded financial fields when available
    const excelData = analysis.opportunities.map((opp, index) => {
      const isGrounded = opp.data_source === 'grounded';
      const row: Record<string, any> = {
        'No.': index + 1,
        'Problem': opp.title,
        'Pattern Type': getInefficiencyLabel(opp.inefficiency_type),
        'Description': opp.description,
        'Affected Steps': opp.affected_steps?.length || 0,
        'Recommended Action': opp.solution_approach,
        'Complexity': opp.implementation_complexity?.toUpperCase() || 'N/A',
        'Time Reduction': opp.estimated_time_reduction || 'N/A',
        'Recommended Tools': opp.primary_tools?.join(', ') || 'N/A',
        'Alternative Tools': opp.alternative_tools?.join(', ') || 'N/A',
        'AI Integration': opp.ai_integration_opportunities || 'N/A',
        // Financial columns
        'Data Source': isGrounded ? 'Grounded' : 'Estimated',
        'Annual Benefit ($)': isGrounded && opp.total_annual_benefit != null ? Number(opp.total_annual_benefit) : '',
        'NPV 3yr ($)': isGrounded && opp.npv_3year != null ? Number(opp.npv_3year) : '',
        'ROI 3yr (%)': isGrounded && opp.roi_3year_pct != null ? Number(opp.roi_3year_pct) : '',
        'Payback (months)': isGrounded && opp.payback_months != null ? Number(opp.payback_months) : '',
        'Annual Hours Saved': isGrounded && opp.annual_hours_saved != null ? Number(opp.annual_hours_saved) : '',
        'Weighted Hourly Rate ($)': isGrounded && opp.weighted_hourly_rate != null ? Number(opp.weighted_hourly_rate) : '',
        // Monte Carlo Sensitivity columns
        'MC NPV P10 ($)': isGrounded && opp.mc_npv_p10 != null ? Number(opp.mc_npv_p10) : '',
        'MC NPV P50 ($)': isGrounded && opp.mc_npv_p50 != null ? Number(opp.mc_npv_p50) : '',
        'MC NPV P90 ($)': isGrounded && opp.mc_npv_p90 != null ? Number(opp.mc_npv_p90) : '',
        'MC ROI P10 (%)': isGrounded && opp.mc_roi_p10 != null ? Number(opp.mc_roi_p10) : '',
        'MC ROI P50 (%)': isGrounded && opp.mc_roi_p50 != null ? Number(opp.mc_roi_p50) : '',
        'MC ROI P90 (%)': isGrounded && opp.mc_roi_p90 != null ? Number(opp.mc_roi_p90) : '',
        'MC Payback P10 (mo)': isGrounded && opp.mc_payback_p10 != null ? Number(opp.mc_payback_p10) : '',
        'MC Payback P50 (mo)': isGrounded && opp.mc_payback_p50 != null ? Number(opp.mc_payback_p50) : '',
        'MC Payback P90 (mo)': isGrounded && opp.mc_payback_p90 != null ? Number(opp.mc_payback_p90) : '',
        'Prob Positive ROI (%)': isGrounded && opp.mc_prob_positive_roi != null ? Number((opp.mc_prob_positive_roi * 100).toFixed(1)) : '',
        'Prob Payback <12mo (%)': isGrounded && opp.mc_prob_payback_12mo != null ? Number((opp.mc_prob_payback_12mo * 100).toFixed(1)) : '',
      };
      return row;
    });

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    ws['!cols'] = [
      { wch: 5 },   // No.
      { wch: 50 },  // Problem
      { wch: 20 },  // Pattern Type
      { wch: 60 },  // Description
      { wch: 15 },  // Affected Steps
      { wch: 60 },  // Recommended Action
      { wch: 12 },  // Complexity
      { wch: 20 },  // Time Reduction
      { wch: 30 },  // Recommended Tools
      { wch: 30 },  // Alternative Tools
      { wch: 40 },  // AI Integration
      { wch: 12 },  // Data Source
      { wch: 18 },  // Annual Benefit ($)
      { wch: 14 },  // NPV 3yr ($)
      { wch: 14 },  // ROI 3yr (%)
      { wch: 18 },  // Payback (months)
      { wch: 20 },  // Annual Hours Saved
      { wch: 22 },  // Weighted Hourly Rate ($)
      { wch: 16 },  // MC NPV P10
      { wch: 16 },  // MC NPV P50
      { wch: 16 },  // MC NPV P90
      { wch: 16 },  // MC ROI P10
      { wch: 16 },  // MC ROI P50
      { wch: 16 },  // MC ROI P90
      { wch: 18 },  // MC Payback P10
      { wch: 18 },  // MC Payback P50
      { wch: 18 },  // MC Payback P90
      { wch: 22 },  // Prob Positive ROI
      { wch: 22 },  // Prob Payback <12mo
    ];

    // Create workbook with two sheets
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Optimization Opportunities');

    // Add Portfolio Summary sheet if any grounded opportunities exist
    const groundedOpps = analysis.opportunities.filter(o => o.data_source === 'grounded');
    if (groundedOpps.length > 0) {
      const totalAnnualBenefit = groundedOpps.reduce((sum, o) => sum + (Number(o.total_annual_benefit) || 0), 0);
      const totalNPV = groundedOpps.reduce((sum, o) => sum + (Number(o.npv_3year) || 0), 0);
      const avgROI = groundedOpps.reduce((sum, o) => sum + (Number(o.roi_3year_pct) || 0), 0) / groundedOpps.length;
      const withPayback = groundedOpps.filter(o => o.payback_months);
      const avgPayback = withPayback.length > 0
        ? withPayback.reduce((sum, o) => sum + (Number(o.payback_months) || 0), 0) / withPayback.length
        : null;

      const summaryData = [
        { 'Metric': 'Grounded Opportunities', 'Value': groundedOpps.length, 'Unit': 'count' },
        { 'Metric': 'Total Opportunities', 'Value': analysis.opportunities.length, 'Unit': 'count' },
        { 'Metric': 'Total Annual Benefit', 'Value': totalAnnualBenefit, 'Unit': 'USD' },
        { 'Metric': 'Portfolio NPV (3yr)', 'Value': totalNPV, 'Unit': 'USD' },
        { 'Metric': 'Average ROI (3yr)', 'Value': Number(avgROI.toFixed(1)), 'Unit': '%' },
        { 'Metric': 'Average Payback Period', 'Value': avgPayback != null ? Number(avgPayback.toFixed(1)) : 'N/A', 'Unit': 'months' },
        { 'Metric': 'Calculation Engine Version', 'Value': '1.0', 'Unit': '' },
        { 'Metric': 'Discount Rate', 'Value': 10, 'Unit': '%' },
        { 'Metric': 'Analysis Horizon', 'Value': 3, 'Unit': 'years' },
        { 'Metric': 'Export Date', 'Value': new Date().toISOString().split('T')[0], 'Unit': '' },
        { 'Metric': '--- Monte Carlo Sensitivity ---', 'Value': '', 'Unit': '' },
        { 'Metric': 'MC Simulations per Opportunity', 'Value': 10000, 'Unit': 'count' },
        { 'Metric': 'Portfolio NPV P10 (Pessimistic)', 'Value': groundedOpps.reduce((sum, o) => sum + (Number(o.mc_npv_p10) || 0), 0), 'Unit': 'USD' },
        { 'Metric': 'Portfolio NPV P50 (Median)', 'Value': groundedOpps.reduce((sum, o) => sum + (Number(o.mc_npv_p50) || 0), 0), 'Unit': 'USD' },
        { 'Metric': 'Portfolio NPV P90 (Optimistic)', 'Value': groundedOpps.reduce((sum, o) => sum + (Number(o.mc_npv_p90) || 0), 0), 'Unit': 'USD' },
        { 'Metric': 'Avg Prob Positive ROI', 'Value': (() => { const mc = groundedOpps.filter(o => o.mc_prob_positive_roi != null); return mc.length > 0 ? Number((mc.reduce((s, o) => s + Number(o.mc_prob_positive_roi), 0) / mc.length * 100).toFixed(1)) : 'N/A'; })(), 'Unit': '%' },
      ];

      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      wsSummary['!cols'] = [{ wch: 30 }, { wch: 20 }, { wch: 10 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Portfolio Summary');
    }

    // Generate file name with project name and date
    const projectName = currentProject?.name || 'Project';
    const date = new Date().toISOString().split('T')[0];
    const fileName = `${projectName}_Optimization_Opportunities_${date}.xlsx`;

    // Download
    await XLSX.writeFile(wb, fileName);
  };

  const downloadMetricsTemplate = async () => {
    try {
      // Extract unique process names from opportunities
      const processNamesSet = new Set(
        analysis?.opportunities?.flatMap(opp =>
          opp.affected_steps?.map(step => step.name || step.stepName) || []
        ).filter(Boolean) || []
      );
      const processNames = Array.from(processNamesSet);

      // Extract opportunity names
      const opportunityNames = analysis?.opportunities?.map(opp => opp.title) || [];

      // Generate and download template
      toast.loading('Generating metrics template...', { id: 'metrics-template' });
      await generateMetricsTemplate(processNames, opportunityNames);
      toast.success('Metrics template downloaded! Fill it out with real client data.', { id: 'metrics-template' });
    } catch (error) {
      console.error('Error generating template:', error);
      toast.error('Failed to generate metrics template', { id: 'metrics-template' });
    }
  };

  const handleDownloadAllReports = async () => {
    if (!analysis || !analysis.opportunities || analysis.opportunities.length === 0) {
      toast.error('No opportunities to generate reports for');
      return;
    }

    if (!projectId || !currentProject) {
      toast.error('Project information is required');
      return;
    }

    try {
      // Generate single comprehensive PDF with all opportunities and all sections
      await generateAllOpportunitiesReport(
        parseInt(projectId),
        currentProject.name,
        analysis.opportunities,
        analysis.analysis.total_steps
      );
    } catch (error) {
      console.error('Error generating reports:', error);
      toast.error('Failed to generate comprehensive report');
    }
  };

  const handleDownloadPptx = async () => {
    if (!analysis || !projectId) return;
    setIsDownloadingPptx(true);
    try {
      toast.loading('Generating PowerPoint deck...', { id: 'pptx' });
      await processOptimizationAPI.downloadPptx(parseInt(projectId), analysis.analysis?.project_id ? projectId : undefined);
      toast.success('PowerPoint downloaded!', { id: 'pptx' });
    } catch (error) {
      console.error('Error generating PowerPoint:', error);
      toast.error('Failed to generate PowerPoint', { id: 'pptx' });
    } finally {
      setIsDownloadingPptx(false);
    }
  };

  // Helper function to create process flow data from opportunity
  const createProcessFlowData = (opportunity: OptimizationOpportunity): any => {
    if (!opportunity.affected_steps || opportunity.affected_steps.length === 0) {
      return undefined;
    }

    // Parse time reduction to get percentages
    const timeReductionMatch = opportunity.estimated_time_reduction.match(/(\d+)%/);
    const reductionPercentage = timeReductionMatch ? parseInt(timeReductionMatch[1]) : 30;

    // Create before steps from affected steps
    const beforeSteps = opportunity.affected_steps.map((step: any, index: number) => ({
      id: step.id?.toString() || `step-${index}`,
      name: step.name || step.processStepName || `Step ${index + 1}`,
      description: step.description || '',
      duration: 60, // Default 60 minutes
      manualEffort: 50, // Default 50 minutes
      owner: step.owner || 'Team Member',
      department: step.department || 'Operations',
      isManual: true,
      isBottleneck: index === 0 // Mark first step as bottleneck for demonstration
    }));

    // Calculate totals for before state
    const beforeTotalDuration = beforeSteps.reduce((sum: number, step: any) => sum + step.duration, 0);
    const beforeTotalEffort = beforeSteps.reduce((sum: number, step: any) => sum + step.manualEffort, 0);

    // Create after steps (automated versions)
    const afterSteps = beforeSteps.map((step: any) => ({
      ...step,
      duration: Math.round(step.duration * (1 - reductionPercentage / 100)),
      manualEffort: Math.round(step.manualEffort * (1 - reductionPercentage / 100)),
      isManual: false,
      isAutomated: true,
      isBottleneck: false,
      automationDetails: opportunity.solution_approach
    }));

    const afterTotalDuration = afterSteps.reduce((sum: number, step: any) => sum + step.duration, 0);
    const afterTotalEffort = afterSteps.reduce((sum: number, step: any) => sum + step.manualEffort, 0);

    // Extract compliance controls from compliance requirements if available
    const complianceControls: any[] = [];
    if (opportunity.compliance_requirements) {
      const reqs = opportunity.compliance_requirements as any;

      // SOX Controls
      if (reqs.sox_controls?.applicable || reqs.sox_controls?.controls) {
        const soxControls = reqs.sox_controls.controls || [];
        if (soxControls.length > 0) {
          complianceControls.push({
            type: 'SOX Controls',
            controls: soxControls.slice(0, 3).map((ctrl: any) => ({
              requirement: ctrl.controlName || ctrl.control || 'Control requirement',
              owner: 'Compliance Team',
              implementation: ctrl.description || 'As per SOX requirements'
            }))
          });
        }
      }

      // Data Governance
      if (reqs.data_governance?.applicable) {
        const dgControls = [];
        if (reqs.data_governance.privacyRequirements) {
          dgControls.push({
            requirement: reqs.data_governance.privacyRequirements[0] || 'Data privacy compliance',
            owner: 'Data Governance',
            implementation: 'Implement data protection controls'
          });
        }
        if (dgControls.length > 0) {
          complianceControls.push({
            type: 'Data Governance',
            controls: dgControls
          });
        }
      }

      // Risk Mitigation
      if (reqs.risk_mitigation?.applicable && reqs.risk_mitigation.failureScenarios) {
        complianceControls.push({
          type: 'Risk Controls',
          controls: reqs.risk_mitigation.failureScenarios.slice(0, 2).map((scenario: any) => ({
            requirement: scenario.scenario,
            owner: 'Risk Management',
            implementation: scenario.mitigationControls?.[0] || scenario.mitigation
          }))
        });
      }
    }

    return {
      opportunityTitle: opportunity.title,
      before: {
        steps: beforeSteps,
        totalDuration: beforeTotalDuration,
        totalManualEffort: beforeTotalEffort,
        totalHandoffs: beforeSteps.length - 1,
        bottlenecks: [beforeSteps[0]?.name || 'Initial process step']
      },
      after: {
        steps: afterSteps,
        totalDuration: afterTotalDuration,
        totalManualEffort: afterTotalEffort,
        totalHandoffs: Math.max(0, afterSteps.length - 2),
        automatedSteps: afterSteps.map((s: any) => s.name)
      },
      transformation: {
        timeSaved: beforeTotalDuration - afterTotalDuration,
        timeSavedPercentage: reductionPercentage,
        effortReduction: beforeTotalEffort - afterTotalEffort,
        effortReductionPercentage: reductionPercentage,
        stepsEliminated: 0,
        stepsAutomated: afterSteps.length,
        handoffsReduced: 1
      },
      complianceControls: complianceControls.length > 0 ? complianceControls : undefined
    };
  };

  // Helper function to calculate total time reduction
  const calculateTotalTimeReduction = (opportunities: OptimizationOpportunity[]): string => {
    let totalPercentage = 0;
    let totalHours = 0;
    let percentageCount = 0;
    let hoursCount = 0;

    opportunities.forEach(opp => {
      const timeReduction = opp.estimated_time_reduction;

      // Check for percentage
      const percentageMatch = timeReduction.match(/(\d+)%/);
      if (percentageMatch) {
        totalPercentage += parseInt(percentageMatch[1]);
        percentageCount++;
      }

      // Check for hours
      const hoursMatch = timeReduction.match(/(\d+)(?:-(\d+))?\s*hours?/i);
      if (hoursMatch) {
        const minHours = parseInt(hoursMatch[1]);
        const maxHours = hoursMatch[2] ? parseInt(hoursMatch[2]) : minHours;
        totalHours += (minHours + maxHours) / 2;
        hoursCount++;
      }
    });

    if (percentageCount > 0 && hoursCount > 0) {
      const avgPercentage = Math.round(totalPercentage / percentageCount);
      return `${avgPercentage}% reduction (${Math.round(totalHours)} hours saved)`;
    } else if (percentageCount > 0) {
      const avgPercentage = Math.round(totalPercentage / percentageCount);
      return `${avgPercentage}% reduction`;
    } else if (hoursCount > 0) {
      return `${Math.round(totalHours)} hours saved`;
    } else {
      return 'Significant time savings expected';
    }
  };

  const sortedOpportunities = useMemo(() => {
    if (!analysis?.opportunities) return [];
    return sortByDependencies(analysis.opportunities);
  }, [analysis?.opportunities]);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('processOptimization_viewMode', mode);
  };

  const filteredOpportunities = useMemo(() => {
    let opportunities = selectedType === 'all'
      ? sortedOpportunities
      : sortedOpportunities.filter(o => o.inefficiency_type === selectedType);

    if (viewMode === 'compliance') {
      opportunities = opportunities.filter(o =>
        o.regulatory_risk_score && o.regulatory_risk_score >= 7
      );
    }

    return opportunities;
  }, [sortedOpportunities, selectedType, viewMode]);

  const patternBreakdown = analysis?.analysis.patterns_detected || {};

  // Auto-compute wizard step based on data state
  const computedWizardStep = useMemo(() => {
    if (!analysis || analysis.opportunities.length === 0) return 1;
    if (groundedStatus && groundedStatus.total > 0 && groundedStatus.grounded >= groundedStatus.total) return 5;
    if (groundedStatus && groundedStatus.grounded > 0) return 4;
    if (Object.keys(dispositions).length >= analysis.opportunities.length) return 3;
    return 2;
  }, [analysis, dispositions, groundedStatus]);

  const [wizardStep, setWizardStep] = useState<1 | 2 | 3 | 4 | 5 | 6>(1);

  useEffect(() => {
    if (wizardStep === 4) loadSkillRegistry();
  }, [wizardStep, loadSkillRegistry]);

  // Sync wizardStep with computed step when data changes (auto-advance)
  useEffect(() => {
    setWizardStep(computedWizardStep as 1 | 2 | 3 | 4 | 5 | 6);
  }, [computedWizardStep]);

  // Step 2 (Disposition) = all opportunities; Step 4 (Govern) = grounded only
  const stepFilteredOpportunities = useMemo(() => {
    if (wizardStep === 4) return filteredOpportunities.filter(o => o.data_source === 'grounded');
    return filteredOpportunities;
  }, [filteredOpportunities, wizardStep]);

  // Show project selection message if no project is available
  if (!projectId) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <Lightbulb className="w-16 h-16 mx-auto mb-4 text-purple-400" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Project Selected</h3>
          <p className="text-gray-600">
            Please select a project from the header to view process optimization analysis.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <RefreshCw className="w-12 h-12 animate-spin text-purple-600" />
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Loading...</h3>
          <p className="text-sm text-gray-600">Please wait...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isWorkflowError = error.includes('workflow') || error.includes('platform');
    return (
      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-3">
          <AlertCircle className="w-6 h-6 text-red-600" />
          <h3 className="text-lg font-semibold text-red-800">Configuration Required</h3>
        </div>
        <p className="text-red-700 mb-4 text-base">{error}</p>
        <div className="flex items-center gap-3">
          {isWorkflowError && (
            <button
              onClick={() => window.location.href = '/app/admin?tab=workflow-tools'}
              className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow font-medium"
            >
              <Settings className="w-4 h-4" />
              Configure Workflow Tools
            </button>
          )}
          <button
            onClick={loadAnalysis}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }


  // Note: !analysis is handled by the wizard (Step 1 shown). Only early-return when analysis ran but found nothing.
  if (analysis && analysis.opportunities.length === 0) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-8 text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
          <CheckCircle className="w-10 h-10 text-blue-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          No Inefficiencies Detected
        </h3>
        <p className="text-gray-700 mb-6 max-w-xl mx-auto">
          Your process appears to be well-optimized. Analysis found {analysis.analysis.total_steps || 0} steps
          with no obvious structural inefficiencies.
        </p>
        <button
          onClick={() => setShowRegenConfirm(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
        >
          <RefreshCw className="w-4 h-4" />
          Re-generate Analysis
        </button>
      </div>
    );
  }

  const oppCount = analysis?.opportunities?.length || 0;
  const orchestratorBanner = orchestratorCtx.inRouter ? (
    <>
    <OrchestratorProjectMismatchBanner />
    <SmartRouterBanner
      stepLabel="Process Optimization"
      canFinish={oppCount > 0}
      disabledReason="Generate or load opportunities before finishing."
      buildCompletionPayload={async () => {
        const payload = (() => {
        const opportunities: any[] = Array.isArray(analysis?.opportunities) ? analysis.opportunities : [];
        const rows = opportunities.map((o: any, i: number) => [
          String(i + 1),
          o?.title || o?.name || 'Opportunity',
          o?.category || o?.type || '',
          o?.impact || o?.expected_impact || '',
          o?.effort || o?.difficulty || '',
          o?.rationale || o?.description || '',
        ]);
        const findings = opportunities.slice(0, 50).map((o: any) => ({
          title: o?.title || o?.name || 'Opportunity',
          detail: o?.description || o?.rationale || '',
          evidence: o?.evidence || o?.source || '',
        }));
        const narrative = opportunities
          .map((o: any, i: number) => {
            const title = o?.title || o?.name || `Opportunity ${i + 1}`;
            const body = o?.description || o?.rationale || '';
            return `### ${i + 1}. ${title}\n\n${body}`;
          })
          .join('\n\n');
        const verifiableText = opportunities
          .map((o: any) => `${o?.title || o?.name || ''}: ${o?.description || o?.rationale || ''}`)
          .join('\n');
        return {
          artifact: {
            refs: [{
              kind: 'optimization_analysis',
              label: `${oppCount} opportunities`,
            }],
            output_payload: {
              opportunities_count: oppCount,
              summary: analysis?.summary || null,
              analysis: {
                narrative,
                findings,
                tables: rows.length > 0
                  ? [{
                      title: 'Optimization Opportunities',
                      columns: ['#', 'Title', 'Category', 'Impact', 'Effort', 'Rationale'],
                      rows,
                    }]
                  : [],
                gaps: [],
              },
              analysis_narrative: narrative,
              optimization: {
                summary: analysis?.summary || null,
                total_steps: analysis?.analysis?.total_steps || null,
                opportunities,
              },
            },
            verifiable_text: verifiableText.slice(0, 50000),
          },
          summary: `Process optimization review: ${oppCount} opportunities surfaced.`,
        };
        })();
        if (!payload) return null;
        await smartRouter.finishStep(payload.artifact, payload.summary);
        return null;
      }}
    />
    </>
  ) : null;

  return (
    <div className="space-y-4">
      {orchestratorBanner}
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <button
            onClick={() => navigate('/app/intelligence')}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-electric-blue mb-1.5 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Jarvis Intelligence
          </button>
          <h1 className="text-2xl font-bold text-super-dark-purple">Process Optimization</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {analysis
              ? <>{analysis.opportunities.length} opportunities · {analysis.analysis.total_steps} steps analyzed</>
              : 'Run AI analysis to get started'}
            {groundedStatus && groundedStatus.total > 0 && (
              <span className="ml-2 text-emerald-600 font-medium">
                · {groundedStatus.grounded}/{groundedStatus.total} grounded
              </span>
            )}
            {reviewedIds.size > 0 && analysis && (
              <span className="ml-2 text-purple-600 font-medium">
                · {reviewedIds.size}/{analysis.opportunities.length} reviewed
              </span>
            )}
          </p>
        </div>
        {verificationMetadata && (
          <VerificationBadge
            metadata={verificationMetadata}
            size="md"
            onClick={() => setShowVerificationPanel(!showVerificationPanel)}
          />
        )}
      </div>
      {showVerificationPanel && verificationMetadata && (
        <VerificationPanel metadata={verificationMetadata} embedded onClose={() => setShowVerificationPanel(false)} />
      )}

      {/* ── Legacy backfilled analysis — pre-redesign data, never reprocessed ── */}
      {isLegacyUngrounded && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-6 py-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">This analysis predates the current Process Optimization engine</p>
            <p className="text-xs text-amber-700 mt-1 leading-relaxed">
              Every opportunity here has no citations and no disposition-engine record — the schema columns were
              backfilled with placeholder values, but this analysis was never re-run through the current engine.
              Rendering it as if it were current is what causes the page to hang. Re-run the assessment to get
              real citations, dispositions, and a working Assess → Exec Story flow.
            </p>
          </div>
          <button
            onClick={() => setShowRegenConfirm(true)}
            className="flex-shrink-0 px-4 py-2 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors whitespace-nowrap"
          >
            Re-run Assessment
          </button>
        </div>
      )}

      {/* ── Wizard Progress Bar ── */}
      <div className="bg-white border border-gray-200 rounded-2xl px-6 py-5 shadow-sm">
        <div className="relative">
          {/* Background track */}
          {(() => {
            const progressStep = Math.max(computedWizardStep, wizardStep);
            return (
              <div className="absolute top-5 left-[8%] right-[8%] h-0.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${Math.max(0, (progressStep - 1) / 5 * 100)}%` }}
                />
              </div>
            );
          })()}
          {/* Step nodes */}
          <div className="relative grid grid-cols-6">
            {([
              { id: 1 as const, label: 'Assess',      desc: 'Scan evidence',          Icon: Lightbulb  },
              { id: 2 as const, label: 'Disposition',  desc: 'Label each opportunity', Icon: GitFork    },
              { id: 3 as const, label: 'Produce',      desc: 'Generate artifact',      Icon: Package    },
              { id: 4 as const, label: 'Govern',       desc: 'Owner & version',        Icon: Shield     },
              { id: 5 as const, label: 'Measure',      desc: 'Matrix & roadmap',       Icon: BarChart3  },
              { id: 6 as const, label: 'Exec Story',   desc: 'Board narrative',        Icon: BookOpen   },
            ]).map((step) => {
              const progressStep = Math.max(computedWizardStep, wizardStep);
              const isCompleted = progressStep > step.id;
              const isActive    = wizardStep === step.id;
              const isLocked    = (step.id === 4 || step.id === 5) && (groundedStatus?.grounded ?? 0) === 0;
              return (
                <button
                  key={step.id}
                  onClick={() => !isLocked && setWizardStep(step.id)}
                  title={isLocked ? 'Upload metrics first to unlock this stage' : undefined}
                  className={`flex flex-col items-center gap-2.5 py-0.5 group focus:outline-none ${isLocked ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all duration-300 ${
                    isActive
                      ? 'bg-purple-600 text-white ring-4 ring-purple-100 shadow-lg shadow-purple-200'
                      : isCompleted
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'bg-white border-2 border-gray-200 text-gray-400 group-hover:border-gray-300 group-hover:text-gray-500'
                  }`}>
                    {isCompleted && !isActive
                      ? <CheckCircle className="w-5 h-5" />
                      : <step.Icon className="w-4 h-4" />
                    }
                  </div>
                  <div className="text-center">
                    <p className={`text-[11px] font-semibold leading-tight transition-colors ${
                      isActive ? 'text-purple-700' : isCompleted ? 'text-gray-700' : 'text-gray-400'
                    }`}>{step.label}</p>
                    <p className={`text-[9px] leading-tight mt-0.5 hidden sm:block transition-colors ${
                      isActive ? 'text-purple-400' : 'text-gray-400'
                    }`}>{step.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Step 1: Assess ── */}
      {wizardStep === 1 && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          {/* Hero — hidden while analyzing */}
          {!analyzing && (
            <div className="bg-gradient-to-br from-purple-50 via-indigo-50 to-white px-10 pt-10 pb-8 text-center border-b border-purple-100">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-sm border border-purple-100 mb-5">
                <Lightbulb className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {analysis ? 'Re-assess Process' : 'Assess Process'}
              </h2>
              <p className="text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
                {analysis
                  ? 'Run a fresh assessment on your evidence. This will replace the current results.'
                  : 'AI scans your evidence — documents, maps, notes — identifies inefficiencies, and surfaces opportunities with citations.'}
              </p>
            </div>
          )}
          {/* Body */}
          <div className="px-10 py-8">
            {analyzing ? (
              /* ── Inline analyzing state ── */
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-purple-100 rounded-2xl mb-5 animate-pulse">
                  <RefreshCw className="w-7 h-7 text-purple-600 animate-spin" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">Assessing Process...</h3>
                <p className="text-sm text-gray-500 mb-6">Detecting inefficiency patterns and generating cited recommendations...</p>
                <div className="max-w-xs mx-auto space-y-2">
                  {[
                    { text: 'Pattern detection',      color: 'bg-blue-500'   },
                    { text: 'ROI estimates',          color: 'bg-green-500'  },
                    { text: 'Compliance analysis',    color: 'bg-orange-500' },
                    { text: 'Implementation roadmap', color: 'bg-purple-500' },
                  ].map(({ text, color }) => (
                    <div key={text} className="flex items-center gap-3 text-left">
                      <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                        <div className={`h-full ${color} rounded-full animate-pulse`} style={{ width: '60%' }} />
                      </div>
                      <span className="text-xs text-gray-500 w-36 flex-shrink-0">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 mb-8 max-w-sm mx-auto">
                  {[
                    { Icon: Search,    text: 'Pattern detection',    color: 'text-blue-600',   bg: 'bg-blue-50'   },
                    { Icon: TrendingUp,text: 'ROI estimates',        color: 'text-green-600',  bg: 'bg-green-50'  },
                    { Icon: Shield,    text: 'Compliance analysis',  color: 'text-orange-600', bg: 'bg-orange-50' },
                    { Icon: Map,       text: 'Implementation roadmap', color: 'text-purple-600', bg: 'bg-purple-50' },
                  ].map(({ Icon, text, color, bg }) => (
                    <div key={text} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${bg}`}>
                        <Icon className={`w-3.5 h-3.5 ${color}`} />
                      </div>
                      <span className="text-xs text-gray-700 font-medium">{text}</span>
                    </div>
                  ))}
                </div>
                <div className="max-w-md mx-auto mb-4">
                  <VerificationToggle
                    mode={verificationMode}
                    onModeChange={setVerificationMode}
                    disabled={analyzing}
                  />
                </div>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => analysis ? setShowRegenConfirm(true) : runAnalysis(false)}
                    className="flex items-center gap-2 px-8 py-3 bg-purple-600 text-white text-sm font-semibold rounded-xl hover:bg-purple-700 active:bg-purple-800 transition-colors shadow-sm"
                  >
                    {analysis ? 'Re-assess' : 'Run Assessment'}
                  </button>
                  {analysis && (
                    <button onClick={() => setWizardStep(2)} className="px-6 py-3 text-sm text-white font-semibold bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors shadow-sm">
                      View results →
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Step 3: Produce ── */}
      {wizardStep === 3 && importHistory.length > 0 && groundedStatus && (() => {
        const latest = importHistory[0];
        const rec = latest.records_imported as any;
        const pct = Number(groundedStatus.percentageGrounded ?? 0);
        const importedAt = new Date(latest.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        return (
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center gap-4 shadow-sm">
            <div className="w-9 h-9 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Upload className="w-4 h-4 text-blue-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">Last Metrics Import</p>
              <p className="text-xs text-gray-400 mt-0.5">{latest.file_name} &bull; {importedAt}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {rec?.laborEconomics > 0 && <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.laborEconomics} labor rates</span>}
                {rec?.processMetrics > 0 && <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.processMetrics} processes</span>}
                {rec?.opportunityMetrics > 0 && <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.opportunityMetrics} opportunities</span>}
                {rec?.opportunitiesUpdated > 0 && <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 font-medium rounded-full">{rec.opportunitiesUpdated} grounded</span>}
              </div>
            </div>
            <div className="flex-shrink-0 flex flex-col items-end gap-1.5 min-w-[180px]">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">Data Quality</span>
                <span className="text-sm font-semibold text-gray-800">{groundedStatus.grounded} / {groundedStatus.total} grounded</span>
                <span className="text-sm font-bold text-emerald-600">{Math.round(pct)}%</span>
                <button onClick={() => setIsImportModalOpen(true)} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors whitespace-nowrap"><Upload className="w-3 h-3" /> Import New</button>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        );
      })()}
      {wizardStep === 3 && (
        <div className="space-y-4">
          {/* Header */}
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-8 pt-7 pb-6 border-b border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Package className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Produce artifact</h2>
                  <p className="text-sm text-gray-500 mt-0.5">Generate a Claude Skill bundle or Custom GPT spec. Approved-tool workflow for automate dispositions. Every artifact includes deterministic checks, citations, and a decision log.</p>
                </div>
              </div>
            </div>

            {/* Per-disposition produce panels */}
            <div className="px-8 py-6 space-y-4">
              {(() => {
                const skillOpps  = sortedOpportunities.filter(o => (dispositions[o.id] || o.disposition) === 'skill_gpt');
                const autoOpps   = sortedOpportunities.filter(o => (dispositions[o.id] || o.disposition) === 'automate');
                const humanOpps  = sortedOpportunities.filter(o => (dispositions[o.id] || o.disposition) === 'keep_human');
                const blockedOpps = sortedOpportunities.filter(o => (dispositions[o.id] || o.disposition) === 'do_not_change');

                const downloadText = (text: string, filename: string) => {
                  const blob = new Blob([text], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = filename; a.click();
                  URL.revokeObjectURL(url);
                };

                const downloadJson = (obj: object, filename: string) => {
                  downloadText(JSON.stringify(obj, null, 2), filename);
                };

                const generateBundle = async (opp: any) => {
                  setGeneratingBundles(prev => ({ ...prev, [opp.id]: true }));
                  try {
                    const bundle = await processOptimizationAPI.generateSkillBundle(opp.id);
                    // A regenerated bundle is a new artifact — approval resets to draft server-side.
                    setSkillGate(prev => ({ ...prev, [opp.id]: { status: 'draft' } }));
                    return bundle;
                  } catch (err: any) {
                    // Fail closed: the generator refuses skills with no citations or with SOD duty consolidation.
                    const code = err?.response?.data?.code;
                    const message = err?.response?.data?.message || err?.message || 'Skill generation failed';
                    toast.error(code === 'CITATIONS_REQUIRED' || code === 'SOD_CONSOLIDATION' ? message : `Skill generation failed: ${message}`, { duration: 7000 });
                    return null;
                  } finally {
                    setGeneratingBundles(prev => ({ ...prev, [opp.id]: false }));
                  }
                };

                const approveSkill = async (opp: any) => {
                  setApprovingSkills(prev => ({ ...prev, [opp.id]: true }));
                  try {
                    const result = await processOptimizationAPI.approveSkill(opp.id);
                    setSkillGate(prev => ({ ...prev, [opp.id]: { status: result.skill_status } }));
                    toast.success('Skill approved for ship — all compliance gates passed');
                  } catch (err: any) {
                    const data = err?.response?.data;
                    if (data?.blocked && Array.isArray(data.failures)) {
                      setSkillGate(prev => ({ ...prev, [opp.id]: { status: 'blocked', failures: data.failures } }));
                      toast.error('Ship gate blocked — see the reasons listed under the skill', { duration: 6000 });
                    } else {
                      toast.error(data?.message || 'Skill approval failed');
                    }
                  } finally {
                    setApprovingSkills(prev => ({ ...prev, [opp.id]: false }));
                  }
                };

                const skillStatusOf = (opp: any) => skillGate[opp.id]?.status || opp.skill_status || 'draft';

                const previewProcessSkill = async () => {
                  if (!projectId) return;
                  setProcessSkillBusy('preview');
                  setProcessSkillError(null);
                  try {
                    setProcessSkill(await processOptimizationAPI.getProcessSkill(Number(projectId)));
                  } catch (err: any) {
                    setProcessSkill(null);
                    setProcessSkillError(err?.response?.data?.message || err?.message || 'Could not build the process skill');
                  } finally {
                    setProcessSkillBusy(null);
                  }
                };

                const downloadProcessSkill = async () => {
                  if (!projectId) return;
                  setProcessSkillBusy('download');
                  try {
                    const { blob, filename } = await processOptimizationAPI.downloadProcessSkill(Number(projectId));
                    const root = processSkill?.files?.[0]?.split('/')[0];
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = filename === 'process-skill.zip' && root ? `${root}.zip` : filename; a.click();
                    URL.revokeObjectURL(url);
                  } catch (err: any) {
                    let message = err?.message || 'Download failed';
                    const data = err?.response?.data;
                    if (data instanceof Blob) {
                      const parsed = await data.text().then(t => JSON.parse(t)).catch(() => null);
                      if (parsed?.message) message = parsed.message;
                    }
                    toast.error(message, { duration: 6000 });
                  } finally {
                    setProcessSkillBusy(null);
                  }
                };

                const runByLabel: Record<string, string> = {
                  agent_skill: 'Agent sub-skill',
                  system_step: 'System',
                  human_checkpoint: 'Human checkpoint',
                  human_step: 'Human',
                };

                return (
                  <>
                    {skillOpps.length > 0 && (
                      <div data-testid="process-skill-panel" className="border border-indigo-200 bg-indigo-50 rounded-xl p-5">
                        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                          <div>
                            <h3 className="text-sm font-semibold text-indigo-900">End-to-end process skill</h3>
                            <p className="text-xs text-indigo-700 mt-1">Stitches the whole process into one skill: each step goes to its sub-skill, the approved workflow tool, or a named human, with a human checkpoint wherever work changes hands.</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              data-testid="process-skill-preview"
                              onClick={previewProcessSkill}
                              disabled={processSkillBusy !== null}
                              className="px-3 py-1.5 text-xs font-medium bg-white border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-60 flex items-center gap-1.5"
                            >
                              {processSkillBusy === 'preview' ? <RefreshCw className="w-3 h-3 animate-spin" /> : null}
                              {processSkill ? 'Refresh preview' : 'Build process skill'}
                            </button>
                            {processSkill && (
                              <button
                                data-testid="process-skill-download"
                                onClick={downloadProcessSkill}
                                disabled={processSkillBusy !== null}
                                className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60 flex items-center gap-1.5"
                              >
                                {processSkillBusy === 'download' ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                Download (.zip)
                              </button>
                            )}
                          </div>
                        </div>
                        {processSkillError && (
                          <p data-testid="process-skill-error" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{processSkillError}</p>
                        )}
                        {processSkill && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 flex-wrap text-[11px]">
                              <span data-testid="process-skill-status" className={`px-2 py-0.5 rounded-full font-semibold ${processSkill.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {processSkill.status === 'approved' ? 'Approved' : 'Draft'}
                              </span>
                              <span className="text-indigo-800">{processSkill.steps.length} steps · {processSkill.counts.agent_skill} agent · {processSkill.counts.human_checkpoint} checkpoints · {processSkill.counts.human_step} human · {processSkill.counts.system_step} system</span>
                              {processSkill.step_order_source === 'opportunities' && (
                                <span className="text-amber-700">Step order derived from the assessed opportunities — re-run the analysis to use the process map order.</span>
                              )}
                            </div>
                            {(processSkill.pending_sub_skills.length > 0 || processSkill.unapproved_sub_skills.length > 0) && (
                              <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-1">
                                <p className="text-[11px] font-semibold text-amber-800">Draft until these are resolved:</p>
                                {processSkill.pending_sub_skills.map(p => (
                                  <p key={`p-${p.opportunity_id}`} className="text-[11px] text-amber-700">{p.title}: {p.reason}</p>
                                ))}
                                {processSkill.unapproved_sub_skills.map(u => (
                                  <p key={`u-${u.opportunity_id}`} className="text-[11px] text-amber-700">{u.title}: sub-skill is {u.skill_status} — approve it for ship.</p>
                                ))}
                              </div>
                            )}
                            <ol className="bg-white border border-indigo-100 rounded-lg divide-y divide-indigo-50">
                              {processSkill.steps.map((s, i) => (
                                <li key={i} className="px-3 py-2 flex items-center gap-3 text-xs">
                                  <span className="w-5 text-right text-gray-400 flex-shrink-0">{i + 1}</span>
                                  <span className="flex-1 min-w-0 truncate text-gray-800">{s.name}{s.actor ? <span className="text-gray-400"> · {s.actor}</span> : null}</span>
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${
                                    s.run_by === 'agent_skill' ? 'bg-purple-100 text-purple-700'
                                      : s.run_by === 'system_step' ? 'bg-blue-100 text-blue-700'
                                        : s.run_by === 'human_checkpoint' ? 'bg-amber-100 text-amber-800'
                                          : 'bg-gray-100 text-gray-600'
                                  }`}>{runByLabel[s.run_by]}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Skill / GPT panel */}
                    {skillOpps.length > 0 && (
                      <div className="border border-purple-200 bg-purple-50 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-base">🧠</span>
                          <h3 className="text-sm font-semibold text-purple-900">Skill / GPT Artifacts ({skillOpps.length})</h3>
                        </div>
                        <p className="text-xs text-purple-700 mb-4">Each opportunity below produces: <strong>SKILL.md</strong> (purpose, hard rules, eval cases, citations) + <strong>gpt_spec.json</strong> (instructions, guardrails, refusals) + <strong>check.js</strong> (deterministic QA script).</p>
                        <div className="space-y-2">
                          {skillOpps.map(opp => (
                            <div key={opp.id} className="bg-white border border-purple-100 rounded-lg px-4 py-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-700 truncate">{opp.title}</p>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${
                                  skillStatusOf(opp) === 'approved'
                                    ? 'bg-green-100 text-green-700'
                                    : skillStatusOf(opp) === 'blocked'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {skillStatusOf(opp) === 'approved' ? 'Approved' : skillStatusOf(opp) === 'blocked' ? 'Blocked' : 'Draft'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {generatingBundles[opp.id] ? (
                                  <span className="text-xs text-purple-500 flex items-center gap-1.5">
                                    <RefreshCw className="w-3 h-3 animate-spin" /> Generating…
                                  </span>
                                ) : (
                                  <>
                                    <button
                                      onClick={async () => {
                                        const bundle = await generateBundle(opp);
                                        if (bundle) downloadText(bundle.skill_md, `SKILL_${opp.id}.md`);
                                      }}
                                      className="px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1.5"
                                    >
                                      <Download className="w-3 h-3" /> SKILL.md
                                    </button>
                                    <button
                                      onClick={async () => {
                                        const bundle = await generateBundle(opp);
                                        if (bundle) downloadJson(bundle.gpt_spec, `gpt_spec_${opp.id}.json`);
                                      }}
                                      className="px-3 py-1.5 text-xs font-medium bg-white border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors flex items-center gap-1.5"
                                    >
                                      <Download className="w-3 h-3" /> GPT Spec
                                    </button>
                                    <button
                                      onClick={async () => {
                                        const bundle = await generateBundle(opp);
                                        if (bundle) downloadText(bundle.check_script, `check_${opp.id}.js`);
                                      }}
                                      className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5"
                                    >
                                      <Download className="w-3 h-3" /> check.js
                                    </button>
                                    {skillStatusOf(opp) !== 'approved' && (
                                      <button
                                        onClick={() => approveSkill(opp)}
                                        disabled={approvingSkills[opp.id]}
                                        title="Compliance ship gate: SOD, MRM tier, data classification, and change management must be recorded and approved"
                                        className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1.5 disabled:opacity-60"
                                      >
                                        {approvingSkills[opp.id] ? 'Checking…' : 'Approve for ship'}
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            {skillGate[opp.id]?.failures && skillGate[opp.id]!.failures!.length > 0 && (
                              <div className="mt-2 border border-red-200 bg-red-50 rounded-lg p-3 space-y-1.5">
                                <p className="text-[11px] font-semibold text-red-800">Ship gate blocked — resolve before approval:</p>
                                {skillGate[opp.id]!.failures!.map((f, i) => (
                                  <p key={i} className="text-[11px] text-red-700">
                                    {f.control_id ? <span className="font-mono font-semibold">[{f.control_id}{f.control_name ? ` — ${f.control_name}` : ''}]</span> : f.framework ? <span className="font-semibold">[{f.framework}]</span> : null} {f.reason}
                                  </p>
                                ))}
                              </div>
                            )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Automate panel — secondary path */}
                    {autoOpps.length > 0 && (
                      <div className="border border-blue-200 bg-blue-50 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-base">🤖</span>
                          <h3 className="text-sm font-semibold text-blue-900">Workflow Automation ({autoOpps.length})</h3>
                        </div>
                        <p className="text-xs text-blue-700 mb-4">Ground AI estimates with real metrics, then generate workflow blueprints on approved platforms.</p>
                        <div className="grid grid-cols-2 gap-3">
                          <button onClick={downloadMetricsTemplate} className="py-2.5 bg-white border border-blue-200 text-xs font-medium text-blue-700 rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center gap-2">
                            <Download className="w-3.5 h-3.5" /> Download Metrics Template
                          </button>
                          <button onClick={() => setIsImportModalOpen(true)} className="py-2.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                            <Upload className="w-3.5 h-3.5" /> Upload Metrics
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Keep Human panel */}
                    {humanOpps.length > 0 && (
                      <div className="border border-amber-200 bg-amber-50 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-base">👤</span>
                          <h3 className="text-sm font-semibold text-amber-900">Human-in-the-Loop Checklist ({humanOpps.length})</h3>
                        </div>
                        <p className="text-xs text-amber-700 mb-4">These steps keep a human sign-off. Download a guardrail checklist with SOD and audit requirements.</p>
                        <div className="space-y-2">
                          {humanOpps.map(opp => (
                            <div key={opp.id} className="bg-white border border-amber-100 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
                              <p className="text-xs font-medium text-gray-700 flex-1 truncate">{opp.title}</p>
                              <button
                                onClick={async () => {
                                  const bundle = await generateBundle(opp);
                                  if (bundle) downloadText(bundle.skill_md, `GUARDRAIL_${opp.id}.md`);
                                }}
                                className="px-3 py-1.5 text-xs font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-1.5 flex-shrink-0"
                              >
                                <Download className="w-3 h-3" /> Guardrail Checklist
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Do Not Change — compliance-protected, intentional */}
                    {blockedOpps.length > 0 && (
                      <div className="border border-gray-200 bg-gray-50 rounded-xl p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-gray-200 flex items-center justify-center text-sm">🛡️</div>
                            <div>
                              <h3 className="text-sm font-semibold text-gray-700">Compliance-Protected Steps</h3>
                              <p className="text-[11px] text-gray-400">These steps require a named human by regulation — no artifact is generated</p>
                            </div>
                          </div>
                          <span className="text-xs font-medium bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{blockedOpps.length} steps</span>
                        </div>
                        <div className="space-y-1.5">
                          {blockedOpps.map(opp => (
                            <div key={opp.id} className="flex items-start gap-2 bg-white border border-gray-100 rounded-lg px-3 py-2">
                              <span className="text-gray-300 mt-0.5 text-xs">—</span>
                              <div className="min-w-0">
                                <p className="text-xs text-gray-600 font-medium leading-snug truncate" title={opp.title}>{opp.title}</p>
                                {opp.disposition_rationale && (
                                  <p className="text-[11px] text-gray-400 mt-0.5">{opp.disposition_rationale}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="px-8 pb-6 flex items-center justify-between">
              <button onClick={() => setWizardStep(2)} className="text-sm text-gray-400 hover:text-gray-700 transition-colors">← Back to Disposition</button>
              <button onClick={() => setWizardStep(4)} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                Next: Govern <ChevronDown className="w-4 h-4 -rotate-90" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 5: Measure ── */}
      {wizardStep === 5 && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-8 pt-6 pb-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Prioritization Matrix</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Impact vs complexity — sequence your opportunities.</p>
                </div>
              </div>
            </div>
            <div className="p-4">
              <PrioritizationMatrix
                opportunities={filteredOpportunities}
                onOpportunityClick={(id) => {
                  const el = document.getElementById(`opportunity-${id}`);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-8 pt-6 pb-4 border-b border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Map className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">Implementation Roadmap</h2>
                <p className="text-xs text-gray-400 mt-0.5">Dependency-sequenced Gantt — when each opportunity ships.</p>
              </div>
            </div>
            <div className="p-4">
              <GanttRoadmap
                opportunities={filteredOpportunities}
                onOpportunityClick={(id) => {
                  const el = document.getElementById(`opportunity-${id}`);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              />
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            <button onClick={() => setWizardStep(4)} className="text-sm text-gray-400 hover:text-gray-600 transition-colors">← Back to Govern</button>
            <button onClick={() => setWizardStep(6)} className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors">
              Next: Exec Story <ChevronDown className="w-4 h-4 -rotate-90" />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 6: Exec Story ── */}
      {wizardStep === 6 && importHistory.length > 0 && groundedStatus && (() => {
        const latest = importHistory[0];
        const rec = latest.records_imported as any;
        const pct = Number(groundedStatus.percentageGrounded ?? 0);
        const importedAt = new Date(latest.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        return (
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center gap-4 shadow-sm">
            <div className="w-9 h-9 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Upload className="w-4 h-4 text-blue-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">Last Metrics Import</p>
              <p className="text-xs text-gray-400 mt-0.5">{latest.file_name} &bull; {importedAt}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {rec?.laborEconomics > 0 && <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.laborEconomics} labor rates</span>}
                {rec?.processMetrics > 0 && <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.processMetrics} processes</span>}
                {rec?.opportunityMetrics > 0 && <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.opportunityMetrics} opportunities</span>}
                {rec?.opportunitiesUpdated > 0 && <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 font-medium rounded-full">{rec.opportunitiesUpdated} grounded</span>}
              </div>
            </div>
            <div className="flex-shrink-0 flex flex-col items-end gap-1.5 min-w-[180px]">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">Data Quality</span>
                <span className="text-sm font-semibold text-gray-800">{groundedStatus.grounded} / {groundedStatus.total} grounded</span>
                <span className="text-sm font-bold text-emerald-600">{Math.round(pct)}%</span>
                <button onClick={() => setIsImportModalOpen(true)} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors whitespace-nowrap"><Upload className="w-3 h-3" /> Import New</button>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        );
      })()}
      {wizardStep === 6 && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          {/* Header */}
          <div className="px-8 pt-8 pb-6 border-b border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Executive Story</h2>
                <p className="text-sm text-gray-500 mt-0.5">One board-ready narrative grounded in your dispositioned opportunities and realized benefits. Export to share.</p>
              </div>
            </div>
          </div>
          {/* Body — the narrative spine first (every number traces to a record), exports below as appendices */}
          <div className="px-8 py-6">
            <ExecutiveStory
              projectId={parseInt(projectId!)}
              refreshKey={storyRefreshKey}
              onOpenOpportunity={(id) => {
                setWizardStep(1);
                setTimeout(() => {
                  const el = document.getElementById(`opportunity-${id}`);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 300);
              }}
            />
            <div className="border-t border-gray-100 mt-8 pt-6">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Appendices &amp; other exports</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <button onClick={exportToExcel} className="flex flex-col items-center gap-3 p-6 bg-gray-50 border border-gray-200 rounded-xl hover:bg-green-50 hover:border-green-300 hover:shadow-sm transition-all group text-center">
                <div className="w-12 h-12 bg-green-100 group-hover:bg-green-200 rounded-xl flex items-center justify-center transition-colors">
                  <Download className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Excel Export</p>
                  <p className="text-xs text-gray-500 mt-0.5">All opportunities with financial data</p>
                </div>
              </button>
              <button onClick={handleDownloadAllReports} className="flex flex-col items-center gap-3 p-6 bg-gray-50 border border-gray-200 rounded-xl hover:bg-purple-50 hover:border-purple-300 hover:shadow-sm transition-all group text-center">
                <div className="w-12 h-12 bg-purple-100 group-hover:bg-purple-200 rounded-xl flex items-center justify-center transition-colors">
                  <FileDown className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">PDF Report</p>
                  <p className="text-xs text-gray-500 mt-0.5">Comprehensive analysis document</p>
                </div>
              </button>
              <button onClick={handleDownloadPptx} disabled={isDownloadingPptx} className="flex flex-col items-center gap-3 p-6 bg-gray-50 border border-gray-200 rounded-xl hover:bg-orange-50 hover:border-orange-300 hover:shadow-sm transition-all group text-center disabled:opacity-50">
                <div className="w-12 h-12 bg-orange-100 group-hover:bg-orange-200 rounded-xl flex items-center justify-center transition-colors">
                  <Monitor className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">PowerPoint</p>
                  <p className="text-xs text-gray-500 mt-0.5">{isDownloadingPptx ? 'Generating...' : 'Presentation deck'}</p>
                </div>
              </button>
              <ExecutiveSummary analysis={analysis!} projectId={parseInt(projectId!)} hideContent={true} />
            </div>
            </div>
            <button onClick={() => setWizardStep(5)} className="text-sm text-gray-400 hover:text-gray-700 transition-colors">← Back to Measure</button>
          </div>
        </div>
      )}

      {/* Last Metrics Import card — show on Step 2 & 4 when import history exists */}
      {(wizardStep === 2 || wizardStep === 4) && importHistory.length > 0 && groundedStatus && (() => {
        const latest = importHistory[0];
        const rec = latest.records_imported as any;
        const pct = Number(groundedStatus.percentageGrounded ?? 0);
        const importedAt = new Date(latest.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        return (
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center gap-4 shadow-sm">
            {/* Icon */}
            <div className="w-9 h-9 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Upload className="w-4 h-4 text-blue-500" />
            </div>

            {/* Left: file info + tags */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">Last Metrics Import</p>
              <p className="text-xs text-gray-400 mt-0.5">{latest.file_name} &bull; {importedAt}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {rec?.laborEconomics > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.laborEconomics} labor rates</span>
                )}
                {rec?.processMetrics > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.processMetrics} processes</span>
                )}
                {rec?.opportunityMetrics > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{rec.opportunityMetrics} opportunities</span>
                )}
                {rec?.opportunitiesUpdated > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 font-medium rounded-full">{rec.opportunitiesUpdated} grounded</span>
                )}
              </div>
            </div>

            {/* Right: data quality + progress + action */}
            <div className="flex-shrink-0 flex flex-col items-end gap-1.5 min-w-[180px]">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">Data Quality</span>
                <span className="text-sm font-semibold text-gray-800">{groundedStatus.grounded} / {groundedStatus.total} grounded</span>
                <span className="text-sm font-bold text-emerald-600">{Math.round(pct)}%</span>
                <button onClick={() => setIsImportModalOpen(true)} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors whitespace-nowrap"><Upload className="w-3 h-3" /> Import New</button>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Steps 2 (Disposition) and 4 (Govern): opportunity lists ── */}
      {(wizardStep === 2 || wizardStep === 4) && (<>

      {/* ── Step 4 (Govern): Skill Registry ── */}
      {wizardStep === 4 && (registryLoading || skillRegistry.length > 0) && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-8 pt-6 pb-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">Skill Registry</h2>
                <p className="text-xs text-gray-400 mt-0.5">Every generated skill — owner, version, freshness against current process docs, and full history.</p>
              </div>
            </div>
            <button onClick={loadSkillRegistry} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Refresh registry">
              <RefreshCw className={`w-4 h-4 text-gray-500 ${registryLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="p-4 space-y-2">
            {registryLoading && skillRegistry.length === 0 && (
              <p className="text-xs text-gray-400 px-2 py-3">Loading registry…</p>
            )}
            {skillRegistry.map(skill => (
              <div key={skill.id} className={`border rounded-xl px-4 py-3 ${skill.skill_status === 'deprecated' ? 'border-gray-200 bg-gray-50 opacity-70' : skill.stale ? 'border-amber-200 bg-amber-50' : 'border-gray-100 bg-white'}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-800 truncate">{skill.title}</p>
                    <span className="text-[10px] font-mono text-gray-400 flex-shrink-0">v{skill.skill_version}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${
                      skill.skill_status === 'approved' ? 'bg-green-100 text-green-700'
                        : skill.skill_status === 'deprecated' ? 'bg-gray-200 text-gray-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {skill.skill_status === 'approved' ? 'Approved' : skill.skill_status === 'deprecated' ? 'Deprecated' : 'Draft'}
                    </span>
                    {skill.stale && skill.skill_status !== 'deprecated' && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 flex-shrink-0">Stale</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <label className="text-[10px] text-gray-400">Owner</label>
                    <input
                      key={`owner-${skill.id}-${skill.skill_owner || ''}`}
                      defaultValue={skill.skill_owner || ''}
                      placeholder="unassigned"
                      disabled={registryBusy[skill.id]}
                      onBlur={(e) => {
                        const next = e.target.value.trim();
                        if (next && next !== (skill.skill_owner || '')) updateGovernance(skill.id, { owner: next });
                      }}
                      className="text-[11px] border border-gray-200 rounded-md px-2 py-1 w-44 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                    />
                    <button
                      onClick={() => updateGovernance(skill.id, { mark_reviewed: true })}
                      disabled={registryBusy[skill.id]}
                      className="px-2.5 py-1 text-[11px] font-medium bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60"
                      title={skill.skill_last_reviewed_at ? `Last reviewed ${new Date(skill.skill_last_reviewed_at).toLocaleDateString()}` : 'Never reviewed'}
                    >
                      Mark reviewed
                    </button>
                    <button
                      onClick={() => updateGovernance(skill.id, { status: skill.skill_status === 'deprecated' ? 'draft' : 'deprecated' })}
                      disabled={registryBusy[skill.id]}
                      className={`px-2.5 py-1 text-[11px] font-medium rounded-lg transition-colors disabled:opacity-60 ${skill.skill_status === 'deprecated' ? 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50' : 'bg-white border border-red-200 text-red-600 hover:bg-red-50'}`}
                    >
                      {skill.skill_status === 'deprecated' ? 'Restore to draft' : 'Deprecate'}
                    </button>
                  </div>
                </div>
                <div className="mt-1.5 flex items-center gap-3 flex-wrap text-[10px] text-gray-400">
                  {skill.skill_bundle_generated_at && <span>Generated {new Date(skill.skill_bundle_generated_at).toLocaleDateString()}</span>}
                  {skill.skill_approved_at && <span>Approved {new Date(skill.skill_approved_at).toLocaleDateString()} by {skill.skill_approved_by}</span>}
                  {skill.skill_last_reviewed_at && <span>Reviewed {new Date(skill.skill_last_reviewed_at).toLocaleDateString()}</span>}
                  {skill.input_hash && <span className="font-mono">hash {skill.input_hash.slice(0, 10)}…</span>}
                  <span>{skill.history.length} event{skill.history.length !== 1 ? 's' : ''} on record</span>
                </div>
                {skill.stale && skill.skill_status !== 'deprecated' && skill.stale_reasons.length > 0 && (
                  <div className="mt-2 border border-amber-200 bg-white rounded-lg px-3 py-2 space-y-0.5">
                    {skill.stale_reasons.map((r, i) => (
                      <p key={i} className="text-[11px] text-amber-700">⚠ {r}</p>
                    ))}
                    <p className="text-[11px] text-amber-600">Regenerate the bundle from Produce so the skill matches current sources, then re-approve.</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Step 4 (Govern): People & Change Impact — a required stage, not a hidden tab ── */}
      {wizardStep === 4 && filteredOpportunities.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-8 pt-6 pb-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">People & Change Impact</h2>
              <p className="text-xs text-gray-400 mt-0.5">What happens to each role — augmentation, not replacement. Judgment stays human wherever it was kept.</p>
            </div>
          </div>
          <div className="p-4 space-y-2">
            {filteredOpportunities.map(opp => {
              const story = (opp as any).change_impact_data?.job_impact_story;
              const kept = opp.disposition === 'keep_human' || opp.disposition === 'do_not_change';
              return (
                <div key={opp.id} className={`border rounded-xl px-4 py-3 ${kept ? 'border-emerald-200 bg-emerald-50' : 'border-gray-100 bg-white'}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-xs font-semibold text-gray-800">{opp.title}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${kept ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'}`}>
                      {opp.disposition === 'skill_gpt' ? 'Skill / GPT' : opp.disposition === 'automate' ? 'Automate' : opp.disposition === 'keep_human' ? 'Judgment stays human' : 'Compliance-protected'}
                    </span>
                  </div>
                  {story ? (
                    <>
                      <p className="text-[11px] text-gray-600 mt-1.5">{story.plain_language}</p>
                      <div className="mt-1.5 grid md:grid-cols-2 gap-x-6 gap-y-0.5 text-[11px]">
                        <p className="text-gray-500"><span className="font-medium text-gray-600">Human keeps:</span> {story.what_human_keeps}</p>
                        <p className="text-gray-500"><span className="font-medium text-gray-600">{kept ? 'AI takes:' : 'Skill takes:'}</span> {story.what_skill_takes}</p>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1">
                        Named owner: <span className="font-medium text-gray-700">{story.named_owner || 'unassigned — assign in the Skill Registry'}</span>
                        {Array.isArray(story.roles_affected) && story.roles_affected.length > 0 && <> · Roles affected: {story.roles_affected.join(', ')}</>}
                      </p>
                      {Array.isArray(story.upskilling_path) && story.upskilling_path.length > 0 && (
                        <p className="text-[11px] text-blue-700 mt-0.5">Upskilling path: {story.upskilling_path.join(' · ')}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-[11px] text-gray-400 mt-1">No change-impact record yet — re-run the analysis (older assessments predate the people story).</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Step 4 (Govern) Stats Cards ── */}
      {wizardStep === 4 && stepFilteredOpportunities.length > 0 && (() => {
        const totalBenefit = stepFilteredOpportunities.reduce((sum, o) => sum + Number(o.total_annual_benefit ?? 0), 0);
        const totalNPV     = stepFilteredOpportunities.reduce((sum, o) => sum + Number(o.npv_3year ?? 0), 0);
        const avgROI       = stepFilteredOpportunities.filter(o => Number(o.roi_3year_pct ?? 0) > 0).reduce((sum, o, _, arr) => sum + Number(o.roi_3year_pct ?? 0) / arr.length, 0);
        const formatCurrency = (v: number) => v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : `$${Math.round(v / 1000)}K`;
        const cards = [
          {
            label: 'Grounded',
            value: stepFilteredOpportunities.length,
            sub: 'Real data verified',
            color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100',
          },
          {
            label: 'Annual Benefit',
            value: totalBenefit > 0 ? formatCurrency(totalBenefit) : '—',
            sub: 'Total per year',
            color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100',
          },
          {
            label: '3-Year NPV',
            value: totalNPV > 0 ? formatCurrency(totalNPV) : '—',
            sub: 'Net present value',
            color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100',
          },
          {
            label: 'Avg ROI',
            value: avgROI > 0 ? `${Math.round(avgROI)}%` : '—',
            sub: '3-year return',
            color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-100',
          },
        ];
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cards.map(({ label, value, sub, color, bg, border }) => (
              <div key={label} className={`${bg} border ${border} rounded-xl px-4 py-4`}>
                <p className="text-xs text-gray-500 mb-1">{label}</p>
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
              </div>
            ))}
          </div>
        );
      })()}
      {/* ── Step 2 (Disposition) Stats Cards ── */}
      {wizardStep === 2 && stepFilteredOpportunities.length > 0 && (() => {
        const dispositioned = Object.keys(dispositions).filter(id => stepFilteredOpportunities.some(o => o.id === Number(id))).length;
        const automateCount = Object.values(dispositions).filter(d => d === 'automate').length;
        const skillCount    = Object.values(dispositions).filter(d => d === 'skill_gpt').length;
        const cards = [
          {
            label: 'To Disposition',
            value: stepFilteredOpportunities.length - dispositioned,
            sub: 'Awaiting label',
            color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-100',
          },
          {
            label: 'Dispositioned',
            value: dispositioned,
            sub: `of ${stepFilteredOpportunities.length} total`,
            color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100',
          },
          {
            label: 'Automate',
            value: automateCount,
            sub: 'Approved tools',
            color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100',
          },
          {
            label: 'Skill / GPT',
            value: skillCount,
            sub: 'AI artifact',
            color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-100',
          },
        ];
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cards.map(({ label, value, sub, color, bg, border }) => (
              <div key={label} className={`${bg} border ${border} rounded-xl px-4 py-4`}>
                <p className="text-xs text-gray-500 mb-1">{label}</p>
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Opportunities List */}
      <div className="space-y-3">

        {(() => {
          const stepCount = stepFilteredOpportunities.length;
          const stepReviewed = stepFilteredOpportunities.filter(o => reviewedIds.has(o.id)).length;
          const allStepReviewed = stepCount > 0 && stepReviewed >= stepCount;
          const allGrounded = groundedStatus && groundedStatus.total > 0 && groundedStatus.grounded >= groundedStatus.total;

          return (
            <>
              {/* Step 2: All dispositioned → nudge to Produce */}
              {wizardStep === 2 && Object.keys(dispositions).length >= stepCount && stepCount > 0 && (
                <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-300 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">All opportunities dispositioned!</p>
                      <p className="text-xs text-gray-500 mt-0.5">Ready to produce artifacts — ground data or generate a Skill/GPT spec.</p>
                    </div>
                  </div>
                  <button onClick={() => setWizardStep(3)} className="px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors flex items-center gap-1.5">
                    Next: Produce <ChevronDown className="w-3 h-3 -rotate-90" />
                  </button>
                </div>
              )}

              {/* Step 4: All grounded → nudge to Measure */}
              {wizardStep === 4 && allGrounded && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-300 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">All opportunities governed with real data!</p>
                      <p className="text-xs text-gray-500 mt-0.5">Review sequencing and roadmap in Measure, then build your Exec Story.</p>
                    </div>
                  </div>
                  <button onClick={() => setWizardStep(5)} className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex items-center gap-1.5">
                    Next: Measure <ChevronDown className="w-3 h-3 -rotate-90" />
                  </button>
                </div>
              )}

              {/* Progress text (Step 2: Disposition) */}
              {wizardStep === 2 && stepCount > 0 && (() => {
                const dCount = Object.keys(dispositions).filter(id => stepFilteredOpportunities.some(o => o.id === Number(id))).length;
                return dCount > 0 && dCount < stepCount ? (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">{`${dCount} of ${stepCount} dispositioned — keep going`}</p>
                    <button onClick={() => setDispositions({})} className="text-xs text-gray-400 hover:text-gray-600 transition-colors ml-auto">
                      Clear all
                    </button>
                  </div>
                ) : null;
              })()}
            </>
          );
        })()}

        {stepFilteredOpportunities.length > 0 ? (
          stepFilteredOpportunities.map((opportunity, index) => (
            <div key={opportunity.id} id={`opportunity-${opportunity.id}`} className="space-y-1">
              {/* Disposition selector — Step 2 only */}
              {wizardStep === 2 && (() => {
                const DISP = [
                  { key: 'automate'       as const, label: '🤖 Automate',      active: 'bg-blue-600 text-white border-blue-600'    },
                  { key: 'skill_gpt'      as const, label: '🧠 Skill / GPT',   active: 'bg-purple-600 text-white border-purple-600' },
                  { key: 'keep_human'     as const, label: '👤 Keep Human',    active: 'bg-amber-500 text-white border-amber-500'   },
                  { key: 'do_not_change'  as const, label: '⏸ Do Not Change', active: 'bg-gray-500 text-white border-gray-500'     },
                ];
                const selected = dispositions[opportunity.id];
                return (
                  <div className="flex items-center gap-2 px-1 flex-wrap">
                    <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide flex-shrink-0">Disposition:</span>
                    {DISP.map(d => (
                      <button
                        key={d.key}
                        onClick={() => persistDisposition(opportunity, d.key)}
                        className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                          selected === d.key ? d.active : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700'
                        }`}
                      >
                        {d.label}
                      </button>
                    ))}
                    {selected && (
                      <button
                        onClick={() => persistDisposition(opportunity, null)}
                        className="text-[10px] text-gray-300 hover:text-gray-500 transition-colors ml-1"
                        title="Revert to the engine's disposition (recorded on the decision trail)"
                      >
                        ✕ revert to engine
                      </button>
                    )}
                    {/* Engine rationale hint */}
                    {(opportunity as any).disposition_rationale && (
                      <span className="text-[10px] text-gray-400 italic ml-1" title={(opportunity as any).disposition_rationale}>
                        ⚙ engine: {(opportunity as any).disposition}
                        {(opportunity as any).disposition_hard_stop ? ' 🚫' : ''}
                      </span>
                    )}
                    {(opportunity as any).disposition_pending_approval && (
                      <span
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 whitespace-nowrap ml-1"
                        title={(opportunity as any).disposition_pending_approval_reason || 'This opportunity ships, but needs a recorded change-approval before it can go live.'}
                      >
                        ⏳ Pending Approval
                      </span>
                    )}
                  </div>
                );
              })()}
              <OpportunityCard
                opportunity={opportunity}
                projectId={parseInt(projectId!)}
                index={index}
                isReviewed={reviewedIds.has(opportunity.id)}
                onMarkReviewed={(id) => setReviewedIds(prev => new Set(Array.from(prev).concat(id)))}
                onOpenImportModal={() => setIsImportModalOpen(true)}
                hideGroundedFinancials={wizardStep === 2}
              />
            </div>
          ))
        ) : wizardStep === 2 ? (
          <div className="space-y-4">
            {/* Cannot-optimize list — first-class, not silent omission */}
            {(() => {
              const blocked = (analysis?.opportunities || []).filter(
                (o: any) => o.disposition === 'do_not_change'
              );
              if (blocked.length === 0) return null;
              return (
                <div className="border border-red-200 bg-red-50 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-red-800 flex items-center gap-2 mb-2">
                    🚫 Where AI cannot optimize ({blocked.length})
                  </h4>
                  <ul className="space-y-1.5">
                    {blocked.map((o: any) => (
                      <li key={o.id} className="text-xs text-red-700">
                        <span className="font-medium">{o.title}</span>
                        {o.disposition_rationale && (
                          <span className="text-red-500 ml-1">— {o.disposition_rationale}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-2xl p-10 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-100 rounded-2xl mb-4">
                <CheckCircle className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">All opportunities dispositioned!</h3>
              <p className="text-sm text-gray-500 mb-5">Every opportunity has a label. Head to Produce to generate artifacts.</p>
              <button onClick={() => setWizardStep(3)} className="px-6 py-2.5 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 transition-colors">
                Go to Produce →
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-xl p-12 text-center">
            {viewMode === 'compliance' ? (
              <div className="max-w-2xl mx-auto">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 bg-green-100 rounded-full">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  No High-Risk Compliance Items Detected
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Compliance View shows automation opportunities that require regulatory review, including:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="text-sm font-semibold text-red-600 mb-1">MRM Tier 1-2</div>
                    <div className="text-xs text-gray-600">AI/ML models requiring validation</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="text-sm font-semibold text-orange-600 mb-1">SOD Violations</div>
                    <div className="text-xs text-gray-600">Segregation of duties conflicts</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="text-sm font-semibold text-yellow-600 mb-1">High Risk Items</div>
                    <div className="text-xs text-gray-600">Critical or high severity issues</div>
                  </div>
                </div>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => {
                      setViewMode('all');
                      localStorage.setItem('processOptimization_viewMode', 'all');
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    View All Opportunities
                  </button>
                  <button
                    onClick={() => {
                      setViewMode('business');
                      localStorage.setItem('processOptimization_viewMode', 'business');
                    }}
                    className="px-4 py-2 bg-white text-gray-700 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Switch to Business View
                  </button>
                </div>
              </div>
            ) : (
              <div className="max-w-md mx-auto">
                <div className="mb-4 flex justify-center">
                  <div className="p-3 bg-gray-200 rounded-full">
                    <AlertCircle className="w-10 h-10 text-gray-500" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Opportunities Match Filter
                </h3>
                <p className="text-gray-600">
                  Try selecting a different pattern type or view mode to see more opportunities.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

        {/* Footer nav — different per step */}
        {wizardStep === 2 && (
          <div className="flex items-center justify-between pt-2">
            <button onClick={() => setWizardStep(1)} className="text-sm text-gray-400 hover:text-gray-600 transition-colors">← Re-assess</button>
            <button onClick={() => setWizardStep(3)} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
              Next: Produce <ChevronDown className="w-4 h-4 -rotate-90" />
            </button>
          </div>
        )}
        {wizardStep === 4 && (
          <div className="flex items-center justify-between pt-2">
            <button onClick={() => setWizardStep(3)} className="text-sm text-gray-400 hover:text-gray-600 transition-colors">← Back to Produce</button>
            <button onClick={() => setWizardStep(5)} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors">
              Next: Measure <ChevronDown className="w-4 h-4 -rotate-90" />
            </button>
          </div>
        )}
      </>)} {/* end wizardStep === 2 or 4 */}

      {/* Re-generate Confirmation Dialog */}
      {showRegenConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <RefreshCw className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Re-generate Analysis?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  This will discard the current analysis and run a fresh AI analysis on your project documents. All existing opportunities and their data will be replaced.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRegenConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowRegenConfirm(false); runAnalysis(true); }}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
              >
                Yes, Re-generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Metrics Import Modal */}
      <MetricsImportModal
        projectId={parseInt(projectId!)}
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

export default ProcessOptimization;
